import { activeSymbols, type FireRedSymbols } from './symbols';

const FRAME_WIDTH = 16;
const FRAME_HEIGHT = 32;
const FRAME_BYTES = (FRAME_WIDTH * FRAME_HEIGHT) / 2; // 4 bits por pixel
const FRAMES = 18; // 9 andando + 9 correndo
const ROM_BASE = 0x08000000;

/**
 * Frames do jogador no mapa, na ordem da pic table do jogo:
 *  0 baixo, 1 cima, 2 esquerda, 3-4 andando p/ baixo, 5-6 p/ cima, 7-8 p/ esquerda,
 *  9-17 o mesmo correndo. Direita = esquerda espelhada.
 */
export type PlayerFrames = HTMLCanvasElement[];

export interface PlayerSprites {
  /** Índice = gênero (0 Red, 1 Leaf). */
  byGender: [PlayerFrames, PlayerFrames];
}

const readU32 = (rom: Uint8Array, offset: number) =>
  (rom[offset] | (rom[offset + 1] << 8) | (rom[offset + 2] << 16) | (rom[offset + 3] << 24)) >>> 0;

function decodePalette(rom: Uint8Array, address: number): number[][] {
  const base = address - ROM_BASE;
  return Array.from({ length: 16 }, (_, i) => {
    const color = rom[base + i * 2] | (rom[base + i * 2 + 1] << 8);
    return [(color & 31) << 3, ((color >> 5) & 31) << 3, ((color >> 10) & 31) << 3];
  });
}

/** Converte um frame 4bpp (tiles 8x8 em sequência, 2 por linha) num canvas. */
function decodeFrame(rom: Uint8Array, offset: number, palette: number[][]): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = FRAME_WIDTH;
  canvas.height = FRAME_HEIGHT;
  const image = new ImageData(FRAME_WIDTH, FRAME_HEIGHT);
  const tilesPerRow = FRAME_WIDTH / 8;
  for (let tile = 0; tile < (FRAME_WIDTH / 8) * (FRAME_HEIGHT / 8); tile++) {
    for (let py = 0; py < 8; py++) {
      for (let px = 0; px < 8; px++) {
        const byte = rom[offset + tile * 32 + py * 4 + (px >> 1)];
        const colorIndex = px & 1 ? byte >> 4 : byte & 15;
        if (colorIndex === 0) continue; // cor 0 é transparente
        const x = (tile % tilesPerRow) * 8 + px;
        const y = Math.floor(tile / tilesPerRow) * 8 + py;
        const i = (y * FRAME_WIDTH + x) * 4;
        const [r, g, b] = palette[colorIndex];
        image.data[i] = r;
        image.data[i + 1] = g;
        image.data[i + 2] = b;
        image.data[i + 3] = 255;
      }
    }
  }
  canvas.getContext('2d')!.putImageData(image, 0, 0);
  return canvas;
}

function decodePicTable(rom: Uint8Array, table: number, palette: number[][]): PlayerFrames {
  const frames: PlayerFrames = [];
  for (let i = 0; i < FRAMES; i++) {
    // struct SpriteFrameImage { const void *data; u32 size; }
    const entry = table - ROM_BASE + i * 8;
    const data = readU32(rom, entry) - ROM_BASE;
    const size = readU32(rom, entry + 4);
    if (size !== FRAME_BYTES || data < 0 || data + size > rom.length) {
      throw new Error(`Frame ${i} inesperado na pic table 0x${table.toString(16)}`);
    }
    frames.push(decodeFrame(rom, data, palette));
  }
  return frames;
}

/** Tira os sprites do Red e da Leaf da própria ROM do jogador. */
export function decodePlayerSprites(rom: Uint8Array, sym: FireRedSymbols = activeSymbols()): PlayerSprites {
  const palette = decodePalette(rom, sym.gObjectEventPal_Player);
  return {
    byGender: [
      decodePicTable(rom, sym.sPicTable_RedNormal, palette),
      decodePicTable(rom, sym.sPicTable_GreenNormal, palette),
    ],
  };
}
